/*
  <tt-reels>: video reels carousel.
  - Cards: [data-reel-open] buttons with data-video (full-quality MP4, for the popup), data-preview
    (lighter full-length MP4 with sound, played inside the card), data-poster and data-title.
  - Autoplay: every card on screen plays its data-preview video muted and looped (paused again when it
    scrolls away), so all reels play as they scroll by. Skipped for data-saver / reduced-motion users,
    who get a still cover until they hover or tap.
  - Hover (mouse): unmutes the card's video; leaving mutes it again. Browsers only allow sound after the
    visitor has clicked/tapped/typed somewhere on the page, so until then hover keeps it muted.
  - Click opens <dialog data-reel-dialog> and plays the full video with sound; closing removes the player.
  - While a video is open, the surrounding <tt-carousel> movement and the card videos are paused.
*/
if (!customElements.get('tt-reels')) {
	class TTReels extends HTMLElement {
		connectedCallback() {
			this.dialog = this.querySelector('[data-reel-dialog]');
			this.frame = this.querySelector('[data-reel-frame]');
			this.caption = this.querySelector('[data-reel-caption]');
			this.carousel = this.querySelector('tt-carousel');
			if (!this.dialog || !this.frame) return;

			this.onClick = (event) => {
				const card = event.target.closest('[data-reel-open]');
				if (card && this.contains(card)) this.open(card);
			};
			this.onBackdrop = (event) => {
				if (event.target === this.dialog) this.dialog.close();
			};
			this.onClosed = () => this.cleanup();

			this.addEventListener('click', this.onClick);
			this.dialog.addEventListener('click', this.onBackdrop);
			this.dialog.addEventListener('close', this.onClosed);
			this.querySelectorAll('[data-reel-close]').forEach((button) =>
				button.addEventListener('click', () => this.dialog.close())
			);

			this.visibleCards = new Set();
			this.setupAutoplay();
			if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) this.setupHoverSound();
		}

		disconnectedCallback() {
			this.removeEventListener('click', this.onClick);
			if (this.autoplayObserver) this.autoplayObserver.disconnect();
			if (this.dialog && this.dialog.open) this.dialog.close();
		}

		// Includes the carousel's repeated (looping) cards.
		get cards() {
			return this.querySelectorAll('[data-reel-open][data-preview]');
		}

		setupAutoplay() {
			const saveData = navigator.connection && navigator.connection.saveData;
			if (saveData || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
			if (!('IntersectionObserver' in window)) return;
			this.autoplay = true;
			this.autoplayObserver = new IntersectionObserver((entries) => {
				entries.forEach((entry) => {
					const card = entry.target;
					if (entry.isIntersecting) {
						this.visibleCards.add(card);
						this.playClip(card);
					} else {
						this.visibleCards.delete(card);
						const clip = card.querySelector('video.tt-reel__autoplay');
						if (clip) {
							clip.muted = true;
							clip.pause();
						}
					}
				});
			}, { threshold: 0.25 });
			this.cards.forEach((card) => this.autoplayObserver.observe(card));
		}

		clipFor(card) {
			let clip = card.querySelector('video.tt-reel__autoplay');
			if (clip) return clip;
			clip = document.createElement('video');
			clip.className = 'tt-reel__autoplay';
			clip.muted = true;
			clip.defaultMuted = true;
			clip.loop = true;
			clip.playsInline = true;
			clip.preload = 'auto';
			clip.setAttribute('muted', '');
			clip.setAttribute('playsinline', '');
			clip.setAttribute('aria-hidden', 'true');
			clip.addEventListener('playing', () => card.classList.add('is-autoplaying'), { once: true });
			clip.src = card.dataset.preview;
			card.prepend(clip);
			return clip;
		}

		playClip(card, withSound = false) {
			if (this.dialog && this.dialog.open) return;
			const clip = this.clipFor(card);
			// Sound is only allowed after the visitor has interacted with the page; otherwise stay muted.
			const mayUnmute = withSound && (!navigator.userActivation || navigator.userActivation.hasBeenActive);
			clip.muted = !mayUnmute;
			const playing = clip.play();
			if (playing && playing.catch) {
				playing.catch(() => {
					if (clip.muted) return;
					clip.muted = true; // blocked with sound: keep playing silently
					const muted = clip.play();
					if (muted && muted.catch) muted.catch(() => {});
				});
			}
		}

		setupHoverSound() {
			this.cards.forEach((card) => {
				card.addEventListener('mouseenter', () => {
					card.classList.add('is-previewing');
					this.playClip(card, true);
				});
				card.addEventListener('mouseleave', () => {
					card.classList.remove('is-previewing');
					const clip = card.querySelector('video.tt-reel__autoplay');
					if (!clip) return;
					clip.muted = true;
					// Without autoplay (data-saver / reduced motion) the video only runs while hovered.
					if (!this.autoplay || !this.visibleCards.has(card)) clip.pause();
				});
			});
		}

		// Pause / resume the card videos around the popup.
		pauseClips() {
			this.querySelectorAll('video.tt-reel__autoplay').forEach((clip) => {
				clip.muted = true;
				clip.pause();
			});
		}

		resumeClips() {
			if (this.autoplay) this.visibleCards.forEach((card) => this.playClip(card));
		}

		open(card) {
			if (!card.dataset.video) return;
			this.frame.replaceChildren();
			const video = document.createElement('video');
			video.src = card.dataset.video;
			video.playsInline = true;
			video.autoplay = true;
			if (card.dataset.poster) video.poster = card.dataset.poster;
			video.controls = true;
			video.setAttribute('controlslist', 'nodownload');
			this.frame.appendChild(video);
			if (this.caption) this.caption.textContent = card.dataset.title || '';

			if (this.carousel) this.carousel.dispatchEvent(new CustomEvent('tt:pause'));
			this.pauseClips();
			document.documentElement.classList.add('tt-reels-open');
			this.dialog.showModal();

			// Started from a click, so playing with sound is allowed.
			const playing = video.play();
			if (playing && playing.catch) playing.catch(() => {});
		}

		cleanup() {
			this.frame.replaceChildren();
			document.documentElement.classList.remove('tt-reels-open');
			if (this.carousel) this.carousel.dispatchEvent(new CustomEvent('tt:resume'));
			this.resumeClips();
		}
	}

	customElements.define('tt-reels', TTReels);
}
